import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  GraduationCap, Plus, Search, Eye, Edit, Trash2, RefreshCw,
  CheckCircle, Clock, AlertCircle, Mail, Phone, Calendar,
  Building2, Briefcase, X, Award, Copy, Send, RotateCcw, Shield, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminTraining = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  
  // Form data
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    designation: "",
    department: "",
    start_date: "",
    send_email: true
  });
  
  // Credentials form data
  const [credentialsForm, setCredentialsForm] = useState({
    crm_platform_name: "",
    crm_url: "",
    crm_username: "",
    crm_password: "",
    email_platform_name: "",
    email_url: "",
    email_username: "",
    email_password: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingCredentials, setIsSubmittingCredentials] = useState(false);
  
  // IP Whitelist states
  const [allowedIPs, setAllowedIPs] = useState([]);
  const [loadingIPs, setLoadingIPs] = useState(false);
  const [showAddIPModal, setShowAddIPModal] = useState(false);
  const [newIPAddress, setNewIPAddress] = useState("");
  const [newIPLabel, setNewIPLabel] = useState("");
  const [isAddingIP, setIsAddingIP] = useState(false);
  const [deletingIPId, setDeletingIPId] = useState(null);
  const [activeTab, setActiveTab] = useState("candidates");

  const getToken = () => localStorage.getItem("admin_token");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  useEffect(() => {
    fetchCandidates();
    fetchAllowedIPs();
  }, []);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/training/candidates`, authHeaders());
      setCandidates(response.data);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error("Failed to load training candidates");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedIPs = async () => {
    setLoadingIPs(true);
    try {
      const response = await axios.get(`${API}/training/allowed-ips`, authHeaders());
      setAllowedIPs(response.data);
    } catch (error) {
      console.error("Error fetching allowed IPs:", error);
      toast.error("Failed to load IP whitelist");
    } finally {
      setLoadingIPs(false);
    }
  };

  const handleAddIP = async (e) => {
    e.preventDefault();
    if (!newIPAddress.trim()) {
      toast.error("Please enter an IP address");
      return;
    }
    
    setIsAddingIP(true);
    try {
      const formData = new FormData();
      formData.append("ip_address", newIPAddress.trim());
      formData.append("label", newIPLabel.trim());
      
      await axios.post(`${API}/training/allowed-ips`, formData, authHeaders());
      toast.success("IP address added to whitelist");
      setShowAddIPModal(false);
      setNewIPAddress("");
      setNewIPLabel("");
      fetchAllowedIPs();
    } catch (error) {
      console.error("Error adding IP:", error);
      toast.error(error.response?.data?.detail || "Failed to add IP address");
    } finally {
      setIsAddingIP(false);
    }
  };

  const handleDeleteIP = async (ipId) => {
    setDeletingIPId(ipId);
    try {
      await axios.delete(`${API}/training/allowed-ips/${ipId}`, authHeaders());
      toast.success("IP address removed from whitelist");
      fetchAllowedIPs();
    } catch (error) {
      console.error("Error deleting IP:", error);
      toast.error(error.response?.data?.detail || "Failed to remove IP address");
    } finally {
      setDeletingIPId(null);
    }
  };

  const handleAddCandidate = () => {
    setSelectedCandidate(null);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      designation: "",
      department: "",
      start_date: new Date().toISOString().split('T')[0],
      send_email: true
    });
    setShowAddModal(true);
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData({
      full_name: candidate.full_name,
      email: candidate.email,
      phone: candidate.phone || "",
      designation: candidate.designation || "",
      department: candidate.department || "",
      start_date: candidate.start_date || "",
      send_email: false
    });
    setShowAddModal(true);
  };

  const handleViewCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setShowViewModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (selectedCandidate) {
        await axios.put(`${API}/training/candidates/${selectedCandidate.id}`, data, authHeaders());
        toast.success("Candidate updated successfully");
      } else {
        const response = await axios.post(`${API}/training/candidates`, data, authHeaders());
        toast.success(`Candidate created! Access code: ${response.data.candidate.access_code}`);
        if (response.data.email_sent) {
          toast.success("Welcome email sent!");
        }
      }
      setShowAddModal(false);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save candidate");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCandidate) return;
    
    try {
      await axios.delete(`${API}/training/candidates/${selectedCandidate.id}`, authHeaders());
      toast.success("Candidate deleted");
      setShowDeleteConfirm(false);
      setSelectedCandidate(null);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to delete candidate");
    }
  };

  const handleApprove = async () => {
    if (!selectedCandidate) return;
    
    try {
      const data = new FormData();
      data.append("notes", approvalNotes);
      
      await axios.post(`${API}/training/candidates/${selectedCandidate.id}/approve`, data, authHeaders());
      toast.success("Training approved!");
      setShowApproveModal(false);
      setApprovalNotes("");
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleResetProgress = async (candidateId, day = null) => {
    try {
      const data = new FormData();
      if (day) data.append("day", day);
      
      await axios.post(`${API}/training/candidates/${candidateId}/reset-progress`, data, authHeaders());
      toast.success(day ? `Day ${day} progress reset` : "All progress reset");
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to reset progress");
    }
  };

  const handleRegenerateCode = async (candidateId) => {
    try {
      const response = await axios.post(`${API}/training/candidates/${candidateId}/regenerate-code`, {}, authHeaders());
      toast.success(`New access code: ${response.data.access_code}`);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to regenerate code");
    }
  };

  const handleResendWelcome = async (candidateId) => {
    try {
      await axios.post(`${API}/training/candidates/${candidateId}/resend-welcome`, {}, authHeaders());
      toast.success("Welcome email sent!");
    } catch (error) {
      toast.error("Failed to send email");
    }
  };

  const handleUploadHandbook = async (candidateId, file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }

    const formData = new FormData();
    formData.append('hr_handbook', file);

    try {
      await axios.post(`${API}/training/candidates/${candidateId}/hr-handbook`, formData, {
        ...authHeaders(),
        headers: {
          ...authHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success("HR Handbook uploaded successfully");
      fetchCandidates();
      // Refresh selected candidate
      const response = await axios.get(`${API}/training/candidates/${candidateId}`, authHeaders());
      setSelectedCandidate(response.data);
    } catch (error) {
      console.error("Error uploading handbook:", error);
      toast.error(error.response?.data?.detail || "Failed to upload handbook");
    }
  };

  const handleDownloadSignedHandbook = async (candidateId) => {
    try {
      const response = await axios.get(`${API}/training/candidates/${candidateId}/signed-handbook`, authHeaders());
      
      // Create download link
      const linkSource = `data:application/pdf;base64,${response.data.data}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();
      
      toast.success("Signed handbook downloaded");
    } catch (error) {
      console.error("Error downloading handbook:", error);
      toast.error(error.response?.data?.detail || "Failed to download signed handbook");
    }
  };

  const handleDeleteHandbook = async (candidateId) => {
    if (!confirm("Are you sure you want to delete the HR Handbook? The candidate will need to re-sign after re-upload.")) {
      return;
    }

    try {
      await axios.delete(`${API}/training/candidates/${candidateId}/hr-handbook`, authHeaders());
      toast.success("HR Handbook deleted");
      fetchCandidates();
      // Refresh selected candidate
      const response = await axios.get(`${API}/training/candidates/${candidateId}`, authHeaders());
      setSelectedCandidate(response.data);
    } catch (error) {
      console.error("Error deleting handbook:", error);
      toast.error("Failed to delete handbook");
    }
  };
  
  const openCredentialsModal = async (candidate) => {
    setSelectedCandidate(candidate);
    // Fetch existing credentials if any
    try {
      const response = await axios.get(`${API}/training/candidates/${candidate.id}/credentials`, authHeaders());
      if (response.data.credentials_added) {
        setCredentialsForm({
          crm_platform_name: response.data.crm_credentials?.platform_name || "",
          crm_url: response.data.crm_credentials?.url || "",
          crm_username: response.data.crm_credentials?.username || "",
          crm_password: response.data.crm_credentials?.password || "",
          email_platform_name: response.data.email_credentials?.platform_name || "",
          email_url: response.data.email_credentials?.url || "",
          email_username: response.data.email_credentials?.username || "",
          email_password: response.data.email_credentials?.password || ""
        });
      } else {
        setCredentialsForm({
          crm_platform_name: "",
          crm_url: "",
          crm_username: "",
          crm_password: "",
          email_platform_name: "",
          email_url: "",
          email_username: "",
          email_password: ""
        });
      }
    } catch (error) {
      console.error("Error fetching credentials:", error);
      setCredentialsForm({
        crm_platform_name: "",
        crm_url: "",
        crm_username: "",
        crm_password: "",
        email_platform_name: "",
        email_url: "",
        email_username: "",
        email_password: ""
      });
    }
    setShowCredentialsModal(true);
  };
  
  const handleSaveCredentials = async () => {
    if (!selectedCandidate) return;
    
    setIsSubmittingCredentials(true);
    try {
      await axios.put(
        `${API}/training/candidates/${selectedCandidate.id}/credentials`,
        credentialsForm,
        authHeaders()
      );
      toast.success("Credentials saved successfully!");
      setShowCredentialsModal(false);
      fetchCandidates();
    } catch (error) {
      console.error("Error saving credentials:", error);
      toast.error(error.response?.data?.detail || "Failed to save credentials");
    } finally {
      setIsSubmittingCredentials(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-500";
      case "completed": return "bg-blue-500";
      case "in_progress": return "bg-amber-500";
      default: return "bg-slate-400";
    }
  };

  const getProgress = (candidate) => {
    let progress = 0;
    if (candidate.day1_quiz_passed) progress += 25;
    if (candidate.day2_quiz_passed) progress += 25;
    if (candidate.day3_quiz_passed) progress += 25;
    if (candidate.handbook_signed) progress += 25;
    return progress;
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = 
      c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: candidates.length,
    pending: candidates.filter(c => c.status === "pending").length,
    inProgress: candidates.filter(c => c.status === "in_progress").length,
    completed: candidates.filter(c => c.status === "completed").length,
    approved: candidates.filter(c => c.status === "approved").length
  };

  return (
    <div className="p-6" data-testid="admin-training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-[#1e3a8a]" />
            Training Portal
          </h1>
          <p className="text-slate-500 mt-1">Manage candidate training and access control</p>
        </div>
      </div>

      {/* Tabs for Candidates and IP Whitelist */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="candidates" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            Candidates
          </TabsTrigger>
          <TabsTrigger value="ip-whitelist" className="flex items-center gap-2" data-testid="ip-whitelist-tab">
            <Shield className="w-4 h-4" />
            IP Whitelist
          </TabsTrigger>
        </TabsList>

        {/* Candidates Tab */}
        <TabsContent value="candidates" className="space-y-6">
          {/* Add Candidate Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleAddCandidate}
              className="bg-[#1e3a8a] hover:bg-[#172554]"
              data-testid="add-training-candidate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </Button>
          </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Pending</p>
            <p className="text-2xl font-bold text-slate-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">In Progress</p>
            <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Completed</p>
            <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-training-candidates"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchCandidates}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Candidates List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1e3a8a] border-t-transparent rounded-full" />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No training candidates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Start Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCandidates.map((candidate) => (
                    <tr key={candidate.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{candidate.full_name}</p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                          <p className="text-xs text-slate-400">{candidate.designation}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="w-32">
                          <div className="flex items-center gap-2 mb-1">
                            <Progress value={getProgress(candidate)} className="h-2" />
                            <span className="text-xs text-slate-500">{getProgress(candidate)}%</span>
                          </div>
                          <div className="flex gap-1">
                            <Badge variant={candidate.day1_quiz_passed ? "default" : "outline"} className="text-xs">D1</Badge>
                            <Badge variant={candidate.day2_quiz_passed ? "default" : "outline"} className="text-xs">D2</Badge>
                            <Badge variant={candidate.day3_quiz_passed ? "default" : "outline"} className="text-xs">D3</Badge>
                            <Badge variant={candidate.handbook_signed ? "default" : "outline"} className="text-xs">HB</Badge>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={getStatusColor(candidate.status)}>
                          {candidate.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">
                        {candidate.start_date || '-'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewCandidate(candidate)}
                            data-testid={`view-training-${candidate.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCandidate(candidate)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {candidate.status === "completed" && !candidate.admin_approved && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600"
                              onClick={() => { setSelectedCandidate(candidate); setShowApproveModal(true); }}
                              data-testid={`approve-training-${candidate.id}`}
                            >
                              <Award className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Add Credentials button - only when training is complete */}
                          {candidate.day3_completed && candidate.day3_quiz_passed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={candidate.credentials_added ? "text-green-600" : "text-amber-600"}
                              onClick={() => openCredentialsModal(candidate)}
                              title={candidate.credentials_added ? "Edit Credentials" : "Add Credentials"}
                              data-testid={`credentials-${candidate.id}`}
                            >
                              <Briefcase className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => { setSelectedCandidate(candidate); setShowDeleteConfirm(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* IP Whitelist Tab */}
        <TabsContent value="ip-whitelist" className="space-y-6">
          <Card className="border-0 shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#1e3a8a]" />
                    IP Whitelist for Training Portal
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Only these IP addresses can access the Training Portal login. 
                    {allowedIPs.length === 0 && (
                      <span className="text-red-500 font-medium"> No IPs whitelisted - all logins are currently blocked!</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchAllowedIPs}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button 
                    onClick={() => setShowAddIPModal(true)}
                    className="bg-[#1e3a8a] hover:bg-[#172554]"
                    data-testid="add-ip-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP Address
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingIPs ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-[#1e3a8a] border-t-transparent rounded-full" />
                </div>
              ) : allowedIPs.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                  <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 mb-2">No IP addresses whitelisted</p>
                  <p className="text-sm text-red-500 mb-4">Training Portal login is blocked for all users</p>
                  <Button onClick={() => setShowAddIPModal(true)} className="bg-[#1e3a8a] hover:bg-[#172554]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First IP Address
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">IP Address</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Added By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Added On</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allowedIPs.map((ip) => (
                        <tr key={ip.id} className="hover:bg-slate-50">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-slate-400" />
                              <span className="font-mono font-medium">{ip.ip_address}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-600">
                            {ip.label || <span className="text-slate-400">-</span>}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500">
                            {ip.added_by || "admin"}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500">
                            {new Date(ip.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteIP(ip.id)}
                                disabled={deletingIPId === ip.id}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Remove IP"
                                data-testid={`delete-ip-${ip.ip_address}`}
                              >
                                {deletingIPId === ip.id ? (
                                  <div className="animate-spin w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How IP Whitelist Works</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Only exact IP addresses are matched (no ranges/wildcards)</li>
                      <li>If no IPs are whitelisted, all Training Portal logins are blocked</li>
                      <li>Other portals (Admin, Pre-Joining, Public Website) are not affected</li>
                      <li>Users from non-whitelisted IPs see: &quot;Login failed. Please contact administrator.&quot;</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add IP Modal */}
      <Dialog open={showAddIPModal} onOpenChange={setShowAddIPModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1e3a8a]" />
              Add IP Address to Whitelist
            </DialogTitle>
            <DialogDescription>
              This IP address will be allowed to access the Training Portal login.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddIP} className="space-y-4">
            <div>
              <Label htmlFor="ip_address">IP Address *</Label>
              <Input
                id="ip_address"
                placeholder="e.g., 192.168.1.100"
                value={newIPAddress}
                onChange={(e) => setNewIPAddress(e.target.value)}
                className="font-mono"
                required
                data-testid="ip-address-input"
              />
              <p className="text-xs text-slate-500 mt-1">Format: xxx.xxx.xxx.xxx</p>
            </div>
            <div>
              <Label htmlFor="ip_label">Label (Optional)</Label>
              <Input
                id="ip_label"
                placeholder="e.g., Office Network, John's Home"
                value={newIPLabel}
                onChange={(e) => setNewIPLabel(e.target.value)}
                data-testid="ip-label-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowAddIPModal(false);
                setNewIPAddress("");
                setNewIPLabel("");
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isAddingIP}
                className="bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="save-ip-btn"
              >
                {isAddingIP ? "Adding..." : "Add IP Address"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedCandidate ? "Edit" : "Add"} Training Candidate</DialogTitle>
            <DialogDescription>
              {selectedCandidate ? "Update candidate details" : "Add a new candidate to the training portal"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="training-candidate-name"
                />
              </div>
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="training-candidate-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  data-testid="training-candidate-start-date"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
              {!selectedCandidate && (
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox
                    id="send_email"
                    checked={formData.send_email}
                    onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
                  />
                  <Label htmlFor="send_email" className="font-normal">
                    Send welcome email with login credentials
                  </Label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="save-training-candidate-btn">
                {isSubmitting ? "Saving..." : selectedCandidate ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Training Candidate Details</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Name</p>
                  <p className="font-medium">{selectedCandidate.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedCandidate.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedCandidate.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Designation</p>
                  <p className="font-medium">{selectedCandidate.designation || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Department</p>
                  <p className="font-medium">{selectedCandidate.department || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Start Date</p>
                  <p className="font-medium">{selectedCandidate.start_date || '-'}</p>
                </div>
              </div>

              {/* Access Code */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Access Code</p>
                    <p className="text-xl font-mono font-bold tracking-widest">{selectedCandidate.access_code}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedCandidate.access_code)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleRegenerateCode(selectedCandidate.id)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleResendWelcome(selectedCandidate.id)}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div>
                <h4 className="font-medium mb-3">Training Progress</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedCandidate.day1_quiz_passed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-slate-400" />
                      )}
                      <span>Day 1: Company Introduction</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedCandidate.day1_quiz_passed ? "default" : "outline"}>
                        Score: {selectedCandidate.day1_quiz_score || 0}%
                      </Badge>
                      {selectedCandidate.day1_quiz_attempts?.length > 0 && (
                        <span className="text-xs text-slate-500">
                          ({selectedCandidate.day1_quiz_attempts.length} attempt{selectedCandidate.day1_quiz_attempts.length > 1 ? 's' : ''})
                        </span>
                      )}
                      {selectedCandidate.day1_quiz_passed && (
                        <Button variant="ghost" size="sm" onClick={() => handleResetProgress(selectedCandidate.id, 1)}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Day 1 Quiz Attempts */}
                  {selectedCandidate.day1_quiz_attempts?.length > 0 && (
                    <div className="ml-8 mb-2 p-2 bg-white rounded border text-xs">
                      <p className="font-medium text-slate-600 mb-1">Quiz Attempts:</p>
                      <div className="space-y-1">
                        {selectedCandidate.day1_quiz_attempts.map((attempt, idx) => (
                          <div key={idx} className="flex justify-between text-slate-500">
                            <span>Attempt {attempt.attempt_number}: {attempt.score}% ({attempt.correct_count}/{attempt.total_questions})</span>
                            <span>{attempt.passed ? '✓ Passed' : '✗ Failed'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedCandidate.day2_quiz_passed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-slate-400" />
                      )}
                      <span>Day 2: US Market & Soft Skills</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedCandidate.day2_quiz_passed ? "default" : "outline"}>
                        Score: {selectedCandidate.day2_quiz_score || 0}%
                      </Badge>
                      {selectedCandidate.day2_quiz_attempts?.length > 0 && (
                        <span className="text-xs text-slate-500">
                          ({selectedCandidate.day2_quiz_attempts.length} attempt{selectedCandidate.day2_quiz_attempts.length > 1 ? 's' : ''})
                        </span>
                      )}
                      {selectedCandidate.day2_quiz_passed && (
                        <Button variant="ghost" size="sm" onClick={() => handleResetProgress(selectedCandidate.id, 2)}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Day 2 Quiz Attempts */}
                  {selectedCandidate.day2_quiz_attempts?.length > 0 && (
                    <div className="ml-8 mb-2 p-2 bg-white rounded border text-xs">
                      <p className="font-medium text-slate-600 mb-1">Quiz Attempts:</p>
                      <div className="space-y-1">
                        {selectedCandidate.day2_quiz_attempts.map((attempt, idx) => (
                          <div key={idx} className="flex justify-between text-slate-500">
                            <span>Attempt {attempt.attempt_number}: {attempt.score}% ({attempt.correct_count}/{attempt.total_questions})</span>
                            <span>{attempt.passed ? '✓ Passed' : '✗ Failed'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedCandidate.day3_quiz_passed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-slate-400" />
                      )}
                      <span>Day 3: Products & Services</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={selectedCandidate.day3_quiz_passed ? "default" : "outline"}>
                        Score: {selectedCandidate.day3_quiz_score || 0}%
                      </Badge>
                      {selectedCandidate.day3_quiz_attempts?.length > 0 && (
                        <span className="text-xs text-slate-500">
                          ({selectedCandidate.day3_quiz_attempts.length} attempt{selectedCandidate.day3_quiz_attempts.length > 1 ? 's' : ''})
                        </span>
                      )}
                      {selectedCandidate.day3_quiz_passed && (
                        <Button variant="ghost" size="sm" onClick={() => handleResetProgress(selectedCandidate.id, 3)}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Day 3 Quiz Attempts */}
                  {selectedCandidate.day3_quiz_attempts?.length > 0 && (
                    <div className="ml-8 mb-2 p-2 bg-white rounded border text-xs">
                      <p className="font-medium text-slate-600 mb-1">Quiz Attempts:</p>
                      <div className="space-y-1">
                        {selectedCandidate.day3_quiz_attempts.map((attempt, idx) => (
                          <div key={idx} className="flex justify-between text-slate-500">
                            <span>Attempt {attempt.attempt_number}: {attempt.score}% ({attempt.correct_count}/{attempt.total_questions})</span>
                            <span>{attempt.passed ? '✓ Passed' : '✗ Failed'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {selectedCandidate.handbook_signed ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                      )}
                      <span>HR Handbook E-Signature</span>
                    </div>
                    <Badge variant={selectedCandidate.handbook_signed ? "default" : "destructive"}>
                      {selectedCandidate.handbook_signed ? `Signed ${selectedCandidate.handbook_signed_at?.split('T')[0] || ''}` : "Pending"}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* HR Handbook Upload Section */}
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  HR Handbook PDF
                </h4>
                
                {selectedCandidate.hr_handbook_uploaded ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{selectedCandidate.hr_handbook_filename}</p>
                          <p className="text-xs text-slate-500">
                            {selectedCandidate.handbook_signed ? (
                              <span className="text-green-600">Signed by candidate</span>
                            ) : (
                              <span className="text-amber-600">Pending signature</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {selectedCandidate.handbook_signed && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadSignedHandbook(selectedCandidate.id)}
                            className="text-green-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Signed PDF
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeleteHandbook(selectedCandidate.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-600">
                      Upload the HR Handbook PDF for this candidate to read and sign.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="file"
                        accept=".pdf"
                        id={`handbook-upload-${selectedCandidate?.id}`}
                        className="text-sm"
                        onChange={(e) => handleUploadHandbook(selectedCandidate.id, e.target.files[0])}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Status & Approval */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`${getStatusColor(selectedCandidate.status)} mt-1`}>
                    {selectedCandidate.status?.replace('_', ' ')}
                  </Badge>
                </div>
                {selectedCandidate.admin_approved ? (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Approved</p>
                    <p className="text-green-600 font-medium flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      {selectedCandidate.admin_approved_at?.split('T')[0]}
                    </p>
                  </div>
                ) : selectedCandidate.status === "completed" ? (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => { setShowViewModal(false); setShowApproveModal(true); }}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Approve Training
                  </Button>
                ) : null}
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="text-red-600"
                  onClick={() => handleResetProgress(selectedCandidate.id)}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset All Progress
                </Button>
                <Button variant="outline" onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <AlertDialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Training</AlertDialogTitle>
            <AlertDialogDescription>
              Approve {selectedCandidate?.full_name}&apos;s training completion?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Notes (optional)</Label>
            <Textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Add any notes..."
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} className="bg-green-600">
              <Award className="w-4 h-4 mr-2" />
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Candidate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedCandidate?.full_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Work Credentials for {selectedCandidate?.full_name}
            </DialogTitle>
            <DialogDescription>
              Add CRM and Email platform credentials. These will be visible to the candidate after they complete training.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* CRM Credentials Section */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                CRM Platform Credentials
              </h4>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Platform Name</Label>
                    <Input
                      placeholder="e.g., Salesforce, HubSpot"
                      value={credentialsForm.crm_platform_name}
                      onChange={(e) => setCredentialsForm({...credentialsForm, crm_platform_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input
                      placeholder="https://crm.example.com"
                      value={credentialsForm.crm_url}
                      onChange={(e) => setCredentialsForm({...credentialsForm, crm_url: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Username</Label>
                    <Input
                      placeholder="Username or Email"
                      value={credentialsForm.crm_username}
                      onChange={(e) => setCredentialsForm({...credentialsForm, crm_username: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="text"
                      placeholder="Password"
                      value={credentialsForm.crm_password}
                      onChange={(e) => setCredentialsForm({...credentialsForm, crm_password: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Email Credentials Section */}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Platform Credentials
              </h4>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Platform Name</Label>
                    <Input
                      placeholder="e.g., Gmail, Outlook"
                      value={credentialsForm.email_platform_name}
                      onChange={(e) => setCredentialsForm({...credentialsForm, email_platform_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input
                      placeholder="https://mail.example.com"
                      value={credentialsForm.email_url}
                      onChange={(e) => setCredentialsForm({...credentialsForm, email_url: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Username</Label>
                    <Input
                      placeholder="Username or Email"
                      value={credentialsForm.email_username}
                      onChange={(e) => setCredentialsForm({...credentialsForm, email_username: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="text"
                      placeholder="Password"
                      value={credentialsForm.email_password}
                      onChange={(e) => setCredentialsForm({...credentialsForm, email_password: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCredentialsModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCredentials} disabled={isSubmittingCredentials}>
              {isSubmittingCredentials ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Credentials
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTraining;
